import "./index.scss";
import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Input, Radio, Space, Tooltip } from "antd";
import { globalVar, isEmptyString, isNullOrUndefined } from "../../common/utils";
import { CommentOutlined, DownOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import AuthZButton from "../AuthzButton";
import { TooltipMessage } from "../../common/data/TooltipMsg";
import {
  useActionOnClickingOutsideOnlyOnCondition,
} from "../../common/hooks/useActionOnClickingOutsideOnlyOnCondition";

const { TextArea } = Input;

function RenderOption({ id, label, isSerious, description, getPopupContainer }) {
  return (
    <Radio value={id}>
      <Tooltip title={description} getPopupContainer={getPopupContainer}>
        <span
          style={{
            backgroundColor: isSerious ? "#F39E0C40" : false,
            padding: "4px",
          }}
        >
          {label}
        </span>
      </Tooltip>
    </Radio>
  );
}

function DropdownRenderer({
                            menuItems = null,
                            setComment,
                            onSave,
                            onCancel,
                            comment,
                            selectedReason,
                            setReason,
                            isCommentMandatory,
                            isSaveDisabled,
                            disabled,
                          }) {

  useEffect(() => {
    if (!disabled) {
      const overlayElement = document.querySelector(".dropdown-overlay");
      if (overlayElement) {
        const overlayRoot = document.querySelector(".fullscreen-enabled")
          ? overlayElement.parentNode.parentNode
          : (document.querySelector(".overlay-root") || overlayElement.parentNode.parentNode);
        overlayRoot.classList.add("overlay-mask");
      }

      return (() => {
        const maskElements = document.querySelectorAll(".overlay-mask");
        maskElements.forEach((element) => element.classList.remove("overlay-mask"));
      });
    }
  }, []);

  function renderComment() {
    if (selectedReason) {
      return (
        <div className="comment">
          <div className="heading">Comment{disabled ? null : <span style={{ color: "red" }}>*</span>}</div>
          <TextArea
            style={{
              borderColor: isCommentMandatory ? "red" : "initial",
              minWidth: "300px",
              height: disabled ? "auto" : "64px",
            }}
            disabled={disabled}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
            className={isCommentMandatory ? "mandatory-review-comment" : "review-comment"}
          />
        </div>
      );
    }
    return null;
  }

  function tooltipMessage() {
    if (isSaveDisabled) {
      if (!menuItems) {
        return TooltipMessage.commentNotModified;
      }
      return (selectedReason && comment) ? TooltipMessage.reasonNotModified : TooltipMessage.reasonNotEntered;
    }
    return null;
  }

  return (
    <div className="reason-comment-dropdown">
      {
        menuItems
          ? (
            <div style={{ padding: "0 16px" }}>
              <div className="heading">Select a reason{disabled ? null :
                <span style={{ color: "red" }}>*</span>}
              </div>
              <Radio.Group
                value={selectedReason?.id}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                size={"small"}
              >
                <Space direction="vertical">
                  {menuItems.map((item) => item)}
                </Space>
              </Radio.Group>
            </div>
          )
          : <div></div>
      }
      {renderComment()}
      {!disabled ?
        <div className="actions">
          <AuthZButton
            authZButtonType="secondary"
            onClick={onCancel}
          >
            Cancel
          </AuthZButton>
          <AuthZButton
            authZButtonType="primary"
            onClick={onSave}
            buttonDisabled={isSaveDisabled}
            tooltipTitle={tooltipMessage()}
          >
            Update
          </AuthZButton>
        </div>
        : null
      }
    </div>
  );
}

function ReasonWithComment({
                             selectedReason,
                             comment,
                             onChange,
                             disabled,
                             taskId,
                             ...restProps
                           }) {
  const ref = useRef(null);
  const reasons = useSelector((rootState) => rootState.appReviewTasks.reasons);
  const editedReviewTasks = useSelector((rootState) => rootState.appReviewTasks.editedReviewTasks);
  const task = editedReviewTasks.find((task) => task.reviewTaskId === taskId);
  const batchSelectedReviewTasks = useSelector((rootState) => rootState.appReviewTasks.batchSelectedReviewTaskKeys);
  const [reason, setReason] = useState(null);
  const [localComment, setLocalComment] = useState("");
  const [reasonDropdownVisible, setReasonDropdownVisible] = useState(false);
  const [commentDropdownVisible, setCommentDropdownVisible] = useState(false);
  const menuItems = reasons
    .filter((reasonOption) => reasonOption.isActive)
    .map((reasonOption) => (
      <RenderOption
        id={reasonOption.id}
        label={reasonOption.name}
        isSerious={reasonOption.isSerious}
        description={reasonOption.description}
        getPopupContainer={restProps.getPopupContainer}
      />
    ));

  useActionOnClickingOutsideOnlyOnCondition(ref, handleClickOutsideDropdowns, disabled);

  useEffect(() => {
    if (!taskId) {
      setReasonDropdownVisible(batchSelectedReviewTasks.some(task => !!task.action && (!task.decisionReason && !task.comment)));
    } else if (batchSelectedReviewTasks.length === 0 && task) {
      setReasonDropdownVisible(!task.decisionReason && !task.comment);
    }
  }, [task?.action, batchSelectedReviewTasks]);

  function getReason(id) {
    return id ? reasons.find((reason) => reason.id === id) : null;
  }

  function getSelectedReason() {
    return getReason(selectedReason?.id);
  }

  function getReasonLabel(id) {
    return getReason(id)?.name;
  }

  function getCurrentComment() {
    return comment ?? "";
  }

  function handleClickOutsideDropdowns() {
    if (reasonDropdownVisible || commentDropdownVisible) {
      onCancel();
    }
  }

  function onSave() {
    onChange({ comment: localComment, decisionReason: { id: reason?.id } });
    setCommentDropdownVisible(false);
    setReasonDropdownVisible(false);
  }

  function onCancel() {
    setReason(getSelectedReason());
    setLocalComment(getCurrentComment());
    setCommentDropdownVisible(false);
    setReasonDropdownVisible(false);
  }

  function isReasonMandatory() {
    return isNullOrUndefined(reason);
  }

  function isCommentMandatory() {
    return isEmptyString(localComment);
  }

  function mandatoryClassName() {
    return (isReasonMandatory() && !disabled) ? " reason-box-mandatory" : "";
  }

  function disabledClassName() {
    return disabled ? " reason-box-disabled" : "";
  }

  function selectedClassName() {
    return reasonDropdownVisible ? " reason-box-selected" : "";
  }

  function isCurrentSelectionSameWithOriginal() {
    return getCurrentComment() === localComment && getSelectedReason()?.id === reason?.id;
  }

  function isSaveDisabled() {
    return isReasonMandatory() || isCommentMandatory() || isCurrentSelectionSameWithOriginal();
  }

  useEffect(() => {
    setReason(getSelectedReason());
    setLocalComment(getCurrentComment());
  }, [selectedReason?.id, comment]);

  return (
    <div ref={ref} className="reason-with-comment">
      <Dropdown
        overlay={
          <DropdownRenderer
            menuItems={menuItems}
            setComment={setLocalComment}
            onSave={onSave}
            onCancel={onCancel}
            comment={localComment}
            selectedReason={reason}
            setReason={(value) => {
              setReason(getReason(parseInt(value)));
            }}
            handleClickOutsideDropdown={handleClickOutsideDropdowns}
            isCommentMandatory={isCommentMandatory()}
            isSaveDisabled={isSaveDisabled()}
            disabled={disabled}
          />
        }
        getPopupContainer={restProps.getPopupContainer}
        overlayClassName={"dropdown-overlay"}
        visible={reasonDropdownVisible}
        destroyPopupOnHide={true}
      >
        <div
          className={"reason-box" + mandatoryClassName() + disabledClassName() + selectedClassName()}
          onClick={() => {
            if (!disabled) {
              setReasonDropdownVisible(true);
            }
          }}
        >
          <div className={"reason-text"}>
            {(reason?.id) ?
              <label title={getReasonLabel(reason.id)}>{getReasonLabel(reason.id)}
              </label> :
              <span className="placeholder">Select reason</span>
            }
          </div>
          {disabled ? null : <DownOutlined style={{ color: "#333333", fontSize: "12px" }} />}
        </div>
      </Dropdown>
      <Dropdown
        overlay={
          <DropdownRenderer
            selectedReason={reason}
            setComment={setLocalComment}
            onSave={onSave}
            onCancel={onCancel}
            comment={localComment}
            handleClickOutsideDropdown={handleClickOutsideDropdowns}
            isCommentMandatory={isCommentMandatory()}
            isSaveDisabled={isSaveDisabled()}
            disabled={disabled}
          />
        }
        overlayClassName={"dropdown-overlay"}
        visible={commentDropdownVisible}
        getPopupContainer={restProps.getPopupContainer}
        destroyPopupOnHide={true}
      >
        <div>
          {comment ?
            <CommentOutlined
              style={{ fontSize: "20px", color: commentDropdownVisible ? "#1f8290" : "grey" }}
              onClick={() => {
                setCommentDropdownVisible(true);
              }}
            />
            : null
          }
        </div>
      </Dropdown>
    </div>
  );
}

function OnlyComment({
                       comment,
                       onChange,
                       disabled,
                     }) {
  const [localComment, setLocalComment] = useState("");

  useEffect(() => {
    setLocalComment(comment ?? "");
  }, [comment]);

  const timeOutId = globalVar.commentTimeoutId;
  // throttle the change in input by timeout of 600ms
  const onChangeForOnlyComment = (e) => {
    const text = e.target.value;
    setLocalComment(text);
    clearTimeout(timeOutId);
    globalVar.commentTimeoutId = setTimeout(() => onChange({ comment: text, decisionReason: null }), 600);
  };

  return (
    <TextArea
      value={localComment}
      style={{
        backgroundColor: "white",
        height: "34px",
      }}
      onChange={(event) => {
        setLocalComment(event.target.value);
        onChangeForOnlyComment(event);
      }}
      placeholder={disabled ? "" : "Add comment"}
      disabled={disabled}
      className="review-comment"
    />
  );
}

export const CommentComponent = ({
                                   selectedReason,
                                   comment,
                                   onChange,
                                   disabled,
                                   onlyComment = true,
                                   taskId = null,
                                   getPopupContainer,
                                 }) => {
  return (
    <>
      {
        onlyComment ?
          <OnlyComment
            comment={comment}
            onChange={onChange}
            disabled={disabled}
            getPopupContainer={getPopupContainer}
          />
          :
          <ReasonWithComment
            selectedReason={selectedReason}
            comment={comment}
            onChange={onChange}
            disabled={disabled}
            taskId={taskId}
            getPopupContainer={getPopupContainer}
          />
      }

    </>
  );
};
